import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Dispatch } from 'redux';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { User } from '../auth/models/user';
import * as fromAuth from '../auth/reducers';
import { CognitoWrapperService } from '../core/services/congito.wrapper.service';
import { ScriptInjectorService } from '../core/services/script-injector.service';
import { environment } from '../../environments/environment';
import { AdminPage } from '../react/pages/admin/AdminPage';
import * as fromRoot from '../reducers';
import { TranslationData } from '../settings/data/settings';
import * as fromSettings from '../settings/reducers';

const containerElementName = 'portalAdminReactContainer';

@Component({
  selector: 'portal-admin-component',
  template: `<div #${containerElementName} class="admin"></div>`,
  styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(containerElementName) containerRef!: ElementRef;

  private _props!: {
    clinicToken: string;
    features: object;
    language: string;
    translations: TranslationData;
    user: User;
    formDesignerLoaded: boolean;
  };

  private _formDesignerLoaded$ = new BehaviorSubject<boolean>(false);
  private _formDesignerScriptRequested = false;

  private _subscriptions = new Subscription();

  constructor(
    private _store: Store<fromRoot.State>,
    private _cognito: CognitoWrapperService,
    private _scriptInjector: ScriptInjectorService
  ) {}

  ngOnInit() {
    this._subscriptions.add(
      this._store
        .pipe(select(fromAuth.getFormDefinitionsEnabled))
        .subscribe((enabled) => {
          if (enabled && !this._formDesignerScriptRequested) {
            this._formDesignerScriptRequested = true;
            this._scriptInjector
              .loadScript(
                'salve-clinic-form-designer',
                environment.formDesignerComponentUrl
              )
              .then(() => this._formDesignerLoaded$.next(true))
              .catch(() => this._formDesignerLoaded$.next(false));
          }
        })
    );

    this._subscriptions.add(
      combineLatest([
        this._store.pipe(select(fromAuth.getClinicId)),
        this._store.pipe(select(fromAuth.getClinicOptions)),
        this._store.pipe(select(fromAuth.getUser)),
        this._store.pipe(select(fromSettings.getCurrentLanguage)),
        this._store.pipe(select(fromSettings.getLanguageTranslations)),
        this._formDesignerLoaded$
      ]).subscribe(
        ([
          clinicToken,
          clinicOptions,
          user,
          language,
          translations,
          formDesignerLoaded
        ]) => {
          this._props = {
            clinicToken,
            features: clinicOptions,
            language,
            translations,
            user,
            formDesignerLoaded
          };
          this.render();
        }
      )
    );
  }

  ngAfterViewInit() {
    this.render();
  }

  ngOnDestroy() {
    ReactDOM.unmountComponentAtNode(this.containerRef.nativeElement);
    this._subscriptions.unsubscribe();
  }

  private async render() {
    ReactDOM.render(
      React.createElement(AdminPage, {
        ...this._props,
        authService: this._cognito,
        dispatch: this._store.dispatch.bind(this._store) as Dispatch
      }),
      this.containerRef.nativeElement
    );
  }
}
